import {
  crypto_box_seal as cryptoBoxSeal,
  crypto_box_keypair as cryptoBoxKeypair,
  crypto_box_seal_open as cryptoBoxSealOpen,
  ready,
} from 'libsodium-wrappers';
import { Buffer } from 'buffer';

export interface IKeypair {
  publicKey: Buffer;
  privateKey?: Buffer;
}

export interface ISerializedKeypair {
  publicKey: string;
  privateKey?: string;
}

export class Asymmetric {
  public static fromSerialized(keypair: ISerializedKeypair): Asymmetric {
    const publicKey = Buffer.from(keypair.publicKey, 'base64');
    const privateKey = keypair.privateKey ? Buffer.from(keypair.privateKey, 'base64') : undefined;
    return new Asymmetric(publicKey, privateKey);
  }

  public static from(keypair: IKeypair): Asymmetric {
    return new Asymmetric(keypair.publicKey, keypair.privateKey);
  }

  public static async generate(): Promise<Asymmetric> {
    await ready;
    const {
      publicKey,
      privateKey,
    } = cryptoBoxKeypair();

    return Asymmetric.from({
      privateKey: Buffer.from(privateKey),
      publicKey: Buffer.from(publicKey),
    });
  }

  public serialize(): ISerializedKeypair {
    return {
      publicKey: this.publicKey.toString('base64'),
      privateKey: this.privateKey?.toString('base64'),
    };
  }

  public async encrypt(message: string): Promise<string> {
    await ready;
    const uInt8Array = cryptoBoxSeal(Buffer.from(message), this.publicKey);
    return Buffer
      .from(uInt8Array)
      .toString('base64');
  }

  public async decrypt(cipher: string): Promise<string> {
    await ready;
    if (!this.privateKey) {
      throw new Error('No private key.');
    }
    const cipherBuffer = Buffer.from(cipher, 'base64');
    const decryptedBytes = cryptoBoxSealOpen(cipherBuffer, this.publicKey, this.privateKey);
    return Buffer
      .from(decryptedBytes)
      .toString();
  }

  // eslint-disable-next-line no-useless-constructor,no-empty-function
  constructor(public publicKey: Buffer, public privateKey?: Buffer) {
  }
}
